import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Permissions } from '../../config';
import useSWR from 'swr';
import { Skeleton, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import { AddAlert, Save } from '@mui/icons-material';

import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';

import Step1 from './AddSteps/Step1.js';
import Step2 from './AddSteps/Step2.js';
import ConfirmStep from './AddSteps/ConfirmStep.js';

import { companyApi, regionApi, companyCategoryApi, fetcher } from 'lib/api';

import wizardStyle from 'assets/jss/material-dashboard-pro-react/components/wizardStyle.js';
import customSelectStyle from 'assets/jss/material-dashboard-pro-react/customSelectStyle.js';
import customCheckboxRadioSwitch from 'assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch.js';
import { createUseStyles } from 'react-jss';

import styles from './addCompany.module.scss';

const jssStyle = {
    inputAdornment: { position: 'relative' },
    colorPickerIcon: {
        position: 'relative', cursor: 'pointer', color: '#de2c49',
        '& svg': { color: '#de2c49' },
    },
    dSelected: { color: '#DE2C48', fontSize: '14px' },
    selectMenu: { color: '#DE2C48', fontSize: '14px' },
    select: { color: '#4c535a', fontSize: '14px' },
    txtArea: {
        width: '100%',
        border: '1px solid rgba(0,0,0,0.23)',
        borderRadius: '8px',
        fontSize: '0.95rem',
        fontFamily: 'Roboto, Helvetica, Arial',
        fontWeight: 400,
        lineHeight: 1.5,
        padding: '10px 14px',
        resize: 'none',
        color: '#3d4148',
        outline: 'none',
        transition: 'border-color 0.15s ease',
        boxSizing: 'border-box',
        '&:hover': { borderColor: 'rgba(0,0,0,0.6)' },
        '&:focus': { borderColor: '#de2c48', borderWidth: '2px' },
    },
    paddingRow: { padding: '15px 15px !important' },
    label: { fontSize: '14px' },
    selectLabel: { fontSize: '14px' },
    social: { width: '40px', height: '40px', color: '#989797', margin: '0px 10px', cursor: 'pointer' },
    socialA: { width: '40px', height: '40px', color: '#00acc1', margin: '0px 10px', cursor: 'pointer' },
    socialTxt: { fontWeight: 'normal', color: '#767676', fontSize: '14px' },
    socialD: { cursor: 'pointer', color: '#cc1a06' },
    tagItem: {
        backgroundColor: '#d4d5d6', fontSize: '14px', borderRadius: '30px',
        height: '30px', padding: '0 4px 0 1rem', display: 'inline-flex',
        alignItems: 'center', margin: '0 0.3rem 0.3rem 0',
    },
    button: {
        backgroundColor: 'white', width: '22px', height: '22px', borderRadius: '50%',
        border: 'none', cursor: 'pointer', font: 'inherit', marginLeft: '10px',
        fontWeight: 'bold', padding: '0', lineHeight: 1, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
    },
    line: { borderColor: '#b6eaf1', height: '1px', marginBottom: 0 },
    rowMargin: { marginTop: '20px' },
    ...wizardStyle,
    ...customSelectStyle,
    ...customCheckboxRadioSwitch,
};

const useStyles = createUseStyles(jssStyle);

const AddCompany = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const { state, search } = useLocation();
    const classes = useStyles();
    const topRef = useRef(null);

    const step1Ref = useRef(null);
    const step2Ref = useRef(null);

    const searchParams = new URLSearchParams(search);
    const focusLocationId = searchParams.get('location');
    const stepParam = searchParams.get('step');
    const initialStep =
        stepParam === 'locations' || stepParam === '1' || focusLocationId ? 1 : 0;

    const [currentStep, setCurrentStep] = useState(initialStep);
    const [isSaving, setIsSaving] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
    const isDirtyRef = useRef(false);
    const [blockedUrl, setBlockedUrl] = useState(null);
    const [showNavBlocker, setShowNavBlocker] = useState(false);

    const markDirty = (val) => {
        isDirtyRef.current = val;
    };

    const localUser = JSON.parse(localStorage.getItem('user'));
    const userPermission = localUser.permission;
    const isSuperAdmin = localUser.roles?.includes('super_admin');
    const canEditAll = userPermission.includes(Permissions.COMPANY_UPDATE);
    const isCompanyAdmin = userPermission.includes(Permissions.CU_COMPANY_UPDATE);
    const companyIds = localUser.companyIds?.filter(Boolean) ?? [];

    useEffect(() => {
        if (id && !isSuperAdmin && !canEditAll && isCompanyAdmin) {
            if (!companyIds.map(String).includes(String(id))) {
                navigate('/companies', { replace: true });
            }
        }
    }, [id]); // eslint-disable-line

    // Deep link: /company/:id?step=locations&location=:locationId
    useEffect(() => {
        if (stepParam === 'locations' || stepParam === '1' || focusLocationId) {
            setCurrentStep(1);
        }
    }, [stepParam, focusLocationId]);

    const isUpdating = !!(state && state.data) || !!id;
    const locationData = (state && state.data) || null;

    const { data: companyData, isLoading: isLoadingCompany, error: companyError, mutate: mutateCompany } = useSWR(
        id ? companyApi.getById(id) : null,
        fetcher
    );
    const { data: regionsData, isLoading: isLoadingRegions } = useSWR(regionApi.list, fetcher);
    const { data: categoriesData, isLoading: isLoadingCategories } = useSWR(companyCategoryApi.list, fetcher);

    const isLoading = (id && isLoadingCompany) || isLoadingRegions;

    const company = companyData?.results
        ? companyData.results
        : locationData
        ? { ...locationData, company_from: 'Torgid' }
        : {};

    const regions = regionsData?.results || regionsData || [];
    const categories = categoriesData?.results || [];

    const stepDefs = [
        { label: t('company.about') },
        { label: t('company.locations') },
        { label: t('confirmation') },
    ];

    const scrollToTop = () => {
        if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const collectPreview = () => {
        const s1 = step1Ref.current?.sendState?.() || null;
        const s2 = step2Ref.current?.sendState?.() || null;
        setPreviewData({ step1: s1, step2: s2 });
    };

    const goNext = () => {
        markDirty(true);
        const next = currentStep + 1;
        if (next === 2) collectPreview();
        setCurrentStep(next);
        scrollToTop();
    };

    const goPrev = () => {
        setCurrentStep((s) => s - 1);
        scrollToTop();
    };

    const goToStep = (i) => {
        if (i === 2 && currentStep < 2) collectPreview();
        setCurrentStep(i);
        scrollToTop();
    };

    const showNotification = (message, type = 'success') => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification((n) => ({ ...n, open: false })), 4000);
    };

    const checkCompany = async (kennitala) => {
        try { await fetcher(companyApi.getByKennitala(kennitala)); } catch { /* ignored */ }
    };

    const handleSave = async () => {
        if (!step1Ref.current || !step2Ref.current || isSaving) return;

        const step1State = step1Ref.current.sendState();
        const step2State = step2Ref.current.sendState();

        const about = { ...step1State };
        if (!about.facebookState && about.facebook) { about.facebook = ''; about.facebookState = true; }
        if (!about.twitterState && about.twitter) { about.twitter = ''; about.twitterState = true; }
        if (!about.instagramState && about.instagram) { about.instagram = ''; about.instagramState = true; }
        if (!about.youtubeState && about.youtube) { about.youtube = ''; about.youtubeState = true; }
        if (!about.pinterestState && about.pinterest) { about.pinterest = ''; about.pinterestState = true; }
        if (!about.linkedinState && about.linkedin) { about.linkedin = ''; about.linkedinState = true; }

        if (about.tagsService) about.tagsService.forEach((v) => about.torgid_company_tags.push({ type: 'service', value: v }));
        if (about.tagsBrand) about.tagsBrand.forEach((v) => about.torgid_company_tags.push({ type: 'brand', value: v }));

        const payload = { torgid_company_locations: step2State.locations, ...about };

        setIsSaving(true);
        try {
            await fetcher(
                isUpdating ? companyApi.update(about.id) : companyApi.create,
                isUpdating ? 'PUT' : 'POST',
                payload
            );
            if (isUpdating) await mutateCompany();
            showNotification(isUpdating ? t('company.updated') : t('company.created'), 'success');
            markDirty(false);
            setTimeout(() => navigate('/companies'), 1000);
        } catch (error) {
            const res = error?.response?.data;
            const msg = res?.results ? res.results[Object.keys(res.results)[0]] : '';
            showNotification((res?.message || t('error')) + (msg ? '. ' + msg : ''), 'warning');
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const originalPushState = window.history.pushState;
        let isInternal = false;

        window.history.pushState = function (state, title, url) {
            if (isInternal || !isDirtyRef.current) {
                originalPushState.call(this, state, title, url);
                return;
            }
            try {
                const next = new URL(url, window.location.origin);
                if (next.pathname === window.location.pathname) {
                    originalPushState.call(this, state, title, url);
                    return;
                }
            } catch { /* ignore */ }
            setBlockedUrl(url?.toString() ?? null);
            setShowNavBlocker(true);
        };

        const handlePopState = () => {
            if (!isDirtyRef.current) return;
            isInternal = true;
            originalPushState.call(window.history, null, '', window.location.href);
            isInternal = false;
            setBlockedUrl(null);
            setShowNavBlocker(true);
        };

        const handleBeforeUnload = (e) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };

        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.history.pushState = originalPushState;
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const confirmLeave = useCallback(() => {
        markDirty(false);
        setShowNavBlocker(false);
        if (blockedUrl) navigate(blockedUrl);
    }, [blockedUrl, navigate]);

    const stayOnPage = useCallback(() => {
        setShowNavBlocker(false);
        setBlockedUrl(null);
    }, []);

    if (id && companyError) {
        return <div className={styles.errorWrap}>Fyrirtæki fannst ekki</div>;
    }

    const sharedProps = {
        classes,
        company,
        data2: categories,
        companyStatus: 'isCompany',
        checkCompany,
        isSuperAdmin,
    };

    return (
        <div ref={topRef}>
            <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 20px' }}>
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
            />
            <GridContainer>
                <GridItem xs={12}>
                    <SparaTitle
                        title={isUpdating ? t('company.update') : t('company.build')}
                        rightComponent={
                            currentStep === stepDefs.length - 1 ? (
                                <SparaButton
                                    color="primary"
                                    icon={Save}
                                    onClick={handleSave}
                                    disabled={isSaving || isLoading}
                                >
                                    {isSaving ? t('saving') : t('save')}
                                </SparaButton>
                            ) : null
                        }
                    />
                </GridItem>
                <GridItem xs={12}>
                    {isLoading ? (
                        <Card style={{ margin: '0' }}>
                            <CardBody>
                                <div className={styles.skeletonWrap}>
                                    <Skeleton variant="rectangular" height={36} />
                                    <Skeleton variant="rectangular" height={48} />
                                    <Skeleton variant="rectangular" height={48} />
                                    <Skeleton variant="rectangular" height={120} />
                                    <Skeleton variant="rectangular" height={48} />
                                </div>
                            </CardBody>
                        </Card>
                    ) : (
                        <>
                            {/* Stepper — outside the card */}
                            <div className={styles.stepper}>
                                {stepDefs.map((step, i) => (
                                    <React.Fragment key={i}>
                                        <div
                                            className={styles.stepItem}
                                            style={{
                                                justifyContent:
                                                    i === 0 ? 'flex-start'
                                                    : i === stepDefs.length - 1 ? 'flex-end'
                                                    : 'center',
                                            }}
                                            onClick={() => { if (i !== currentStep) goToStep(i); }}
                                        >
                                            <div className={[
                                                styles.stepCircle,
                                                i === currentStep ? styles.stepCircleActive : '',
                                                i < currentStep ? styles.stepCircleDone : '',
                                            ].join(' ')}>
                                                {i < currentStep ? '✓' : i + 1}
                                            </div>
                                            <span className={[
                                                styles.stepLabel,
                                                i === currentStep ? styles.stepLabelActive : '',
                                                i < currentStep ? styles.stepLabelDone : '',
                                            ].join(' ')}>
                                                {step.label}
                                            </span>
                                        </div>
                                        {i < stepDefs.length - 1 && (
                                            <div className={[
                                                styles.stepLine,
                                                i < currentStep ? styles.stepLineDone : '',
                                            ].join(' ')} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Step content */}
                            <div className={styles.stepContent}>
                                <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
                                    <Step1
                                        key={company.id || 'new'}
                                        ref={step1Ref}
                                        {...sharedProps}
                                        onFieldChange={() => markDirty(true)}
                                    />
                                </div>
                                <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
                                    <Step2
                                        key={company.id || 'new'}
                                        ref={step2Ref}
                                        classes={classes}
                                        company={company}
                                        data={regions}
                                        focusLocationId={focusLocationId}
                                        onFieldChange={() => markDirty(true)}
                                    />
                                </div>
                                {currentStep === 2 && (
                                    <ConfirmStep
                                        step1Data={previewData?.step1}
                                        step2Data={previewData?.step2}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </GridItem>
            </GridContainer>
            </div>

            {!isLoading && (
                <div className={styles.bottomNav}>
                    <div>
                        {currentStep > 0 && (
                            <button className={`${styles.navBtn} ${styles.navBtnPrev}`} onClick={goPrev}>
                                ← {t('previous')}
                            </button>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {currentStep < stepDefs.length - 1 ? (
                            <button className={`${styles.navBtn} ${styles.navBtnNext}`} onClick={goNext}>
                                {t('next')} →
                            </button>
                        ) : (
                            <button
                                className={`${styles.navBtn} ${styles.navBtnSave}`}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? t('saving') : t('save')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            <Dialog open={showNavBlocker} maxWidth="xs" fullWidth>
                <DialogTitle>{t('unsaved_changes_title')}</DialogTitle>
                <DialogContent>
                    <p style={{ margin: 0, fontSize: '14px', color: 'var(--spara-text, #333)' }}>
                        {t('unsaved_changes_message')}
                    </p>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={stayOnPage}>{t('unsaved_stay')}</MuiButton>
                    <MuiButton onClick={confirmLeave} color="error">
                        {t('unsaved_leave')}
                    </MuiButton>
                    <MuiButton
                        onClick={async () => {
                            await handleSave();
                            confirmLeave();
                        }}
                        variant="contained"
                        color="success"
                    >
                        {t('save')}
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AddCompany;
